import type { CSSProperties, ReactNode } from 'react';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';

import styles from './ExposeSkeletonLoading.module.css';

type SkeletonDivProps = {
  width?: string;
  height?: string;
  borderRadius?: string;
};

const SkeletonDiv = ({ width, height, borderRadius }: SkeletonDivProps): ReactNode => {
  return (
    <div
      className={styles.skeletonDiv}
      style={
        {
          '--skeleton-width': width,
          '--skeleton-height': height,
          '--skeleton-border-radius': borderRadius,
        } as CSSProperties
      }
    />
  );
};

type ContentContainerProps = {
  children: ReactNode;
  flexDirection?: string;
  justifyContent?: string;
  flex?: number;
  display?: string;
};

const ContentContainer = ({ children, flexDirection, justifyContent, flex, display }: ContentContainerProps): ReactNode => {
  return (
    <div
      className={styles.contentContainer}
      data-mobile-display={display}
      style={
        {
          '--content-flex': flex,
          '--content-flex-direction': flexDirection,
          '--content-justify': justifyContent,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
};

const ActionBarSkeleton = (): ReactNode => {
  return (
    <div className={styles.actionBarSkeletonContainer}>
      <SkeletonDiv height="var(--distance-static-xxl)" width="var(--distance-static-3xl)" />
      <div className={styles.actionButtonRightContainer}>
        <SkeletonDiv height="var(--distance-static-xxl)" width="var(--distance-static-3xl)" />
        <SkeletonDiv height="var(--distance-static-xxl)" width="var(--distance-static-3xl)" />
      </div>
    </div>
  );
};

export const ExposeSkeletonLoading = ({ isPrivateOffice }: { isPrivateOffice: boolean }): ReactNode => {
  return (
    <>
      {isPrivateOffice ? (
        <>
          <div className={styles.fullWidthSkeletonNavigationContainer}>
            <ActionBarSkeleton />
          </div>
          <div className={styles.privateOfficeHeroImageSkeletonContainer}>
            <div className={styles.fullWidthSkeleton} />
          </div>
        </>
      ) : (
        <Wrapper className={styles.wrapper} width={{ tabletLandscape: 'l', laptop: 'l' }}>
          <ActionBarSkeleton />

          <div className={styles.imageSkeletonContainer}>
            <div className={styles.leftColumn}>
              <SkeletonDiv />
            </div>

            <div className={styles.rightColumn}>
              <SkeletonDiv />
              <SkeletonDiv />
            </div>
          </div>
        </Wrapper>
      )}
      <Wrapper className={styles.wrapper} width={{ laptop: 'l', tabletLandscape: 'l' }}>
        <div className={styles.contentWrapper}>
          <ContentContainer>
            <SkeletonDiv height="var(--distance-static-m)" width="40%" />
            <SkeletonDiv height="var(--distance-static-l)" width="80%" />
            <SkeletonDiv height="var(--distance-static-l)" width="50%" />
            <SkeletonDiv height="var(--distance-static-l)" width="50%" />

            <SkeletonDiv height="var(--distance-static-xxs)" width="100%" />
            <ContentContainer flex={0} flexDirection="row">
              <SkeletonDiv height="var(--distance-static-l)" />
              <SkeletonDiv height="var(--distance-static-l)" />
              <SkeletonDiv height="var(--distance-static-l)" />
            </ContentContainer>

            <SkeletonDiv height="var(--distance-static-xxs)" width="100%" />

            <SkeletonDiv height="var(--distance-static-l)" width="50%" />
            <SkeletonDiv height="var(--distance-static-l)" width="60%" />
            {Array.from({ length: 4 })
              .fill(0)
              .map((_, index) => (
                <SkeletonDiv key={index} height="var(--distance-static-l)" width="100%" />
              ))}
          </ContentContainer>

          <ContentContainer display="none" justifyContent="flex-end" flexDirection="row">
            <SkeletonDiv height="40vh" width="50%" />
          </ContentContainer>
        </div>
      </Wrapper>
    </>
  );
};
